import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { ScrollToTop } from "./components/ScrollToTop";
import { Home } from "./pages/Home";
import { TrainingPrograms } from "./pages/TrainingPrograms";
import { AboutUs } from "./pages/AboutUs";
import { Contact } from "./pages/Contact";
import { CertificateValidationPage } from "./pages/CertificateValidationPage";
import { TrainingCalendarPublic } from "./pages/TrainingCalendarPublic";
import { ManagementSystems } from "./pages/ManagementSystems";
import { StandardDetail } from "./pages/StandardDetail";
import { CourseDetail } from "./pages/CourseDetail";
import { TrainingCategoryDetail } from "./pages/TrainingCategoryDetail";
import { Placement } from "./pages/Placement";
import { ProfessionalDevelopment } from "./pages/ProfessionalDevelopment";
import { SoftSkillsTraining } from "./pages/SoftSkillsTraining";
import { LeadershipManagementTraining } from "./pages/LeadershipManagementTraining";
import { CommunicationSkillsTraining } from "./pages/CommunicationSkillsTraining";
import { FacultyDevelopmentProgram } from "./pages/FacultyDevelopmentProgram";
import { HEITraining } from "./pages/HEITraining";
import { ISO9001LeadAuditor } from "./pages/ISO9001LeadAuditor";
import { ISO27001LeadAuditor } from "./pages/ISO27001LeadAuditor";
import { ISO14001LeadAuditor } from "./pages/ISO14001LeadAuditor";
import { ISO45001LeadAuditor } from "./pages/ISO45001LeadAuditor";
import { FMEATraining } from "./pages/FMEATraining";
import { CoreToolsTraining } from "./pages/CoreToolsTraining";
import { ISO9001Awareness } from "./pages/ISO9001Awareness";
import { ISO9001InternalAuditor } from "./pages/ISO9001InternalAuditor";
import { ISO14001Awareness } from "./pages/ISO14001Awareness";
import { ISO14001InternalAuditor } from "./pages/ISO14001InternalAuditor";
import { ISO27001Awareness } from "./pages/ISO27001Awareness";
import { ISO27001InternalAuditor } from "./pages/ISO27001InternalAuditor";
import { ISO45001Awareness } from "./pages/ISO45001Awareness";
import { ISO45001InternalAuditor } from "./pages/ISO45001InternalAuditor";
import { IATF16949Awareness } from "./pages/IATF16949Awareness";
import { IATF16949InternalAuditor } from "./pages/IATF16949InternalAuditor";
import { ISO13485Awareness } from "./pages/ISO13485Awareness";
import { ISO13485InternalAuditor } from "./pages/ISO13485InternalAuditor";
import { ISO13485LeadAuditor } from "./pages/ISO13485LeadAuditor";
import { LeanSixSigma } from "./pages/LeanSixSigma";
import { TotalProductiveMaintenance } from "./pages/TotalProductiveMaintenance";
import { ESGTraining } from "./pages/ESGTraining";
import { ISO22000Awareness } from "./pages/ISO22000Awareness";
import { ISO22000InternalAuditor } from "./pages/ISO22000InternalAuditor";
import { ISO50001Awareness } from "./pages/ISO50001Awareness";
import { ISO50001InternalAuditor } from "./pages/ISO50001InternalAuditor";
import { ISO21001Awareness } from "./pages/ISO21001Awareness";
import { ISO21001InternalAuditor } from "./pages/ISO21001InternalAuditor";
import { ISO21001LeadAuditor } from "./pages/ISO21001LeadAuditor";
import { ISO22000LeadAuditor } from "./pages/ISO22000LeadAuditor";
import { ISO50001LeadAuditor } from "./pages/ISO50001LeadAuditor";
import { IATF16949LeadAuditor } from "./pages/IATF16949LeadAuditor";
import TrainingDetailPage from "./pages/TrainingDetailPage";
import { IndustryTrainings } from "./pages/IndustryTrainings";
import { IndustryDetail } from "./pages/IndustryDetail";
import { TrainingProgramDetail } from "./pages/TrainingProgramDetail";
import { NABHTraining } from "./pages/NABHTraining";
import { FiveSTraining } from "./pages/FiveSTraining";
import { AITraining } from "./pages/AITraining";
import { NAACAccreditationPreparation } from "./pages/NAACAccreditationPreparation";
import { OBEImplementation } from "./pages/OBEImplementation";
import { ISO21001EducationalOrganizations } from "./pages/ISO21001EducationalOrganizations";
import { ResearchMethodology } from "./pages/ResearchMethodology";
import { DigitalTeachingTools } from "./pages/DigitalTeachingTools";
import { CorporateServicesDetail } from "./pages/CorporateServicesDetail";

// Admin imports
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminLogin } from "./pages/admin/Login";
import { AdminLayout } from "./components/admin/AdminLayout";
import { AdminDashboard } from "./pages/admin/Dashboard";
import { TrainingCalendarAdmin } from "./pages/admin/TrainingCalendar";
import { Registrations } from "./pages/admin/Registrations";
import { ContactSubmissions } from "./pages/admin/ContactSubmissions";
import { EmailSettings } from "./pages/admin/EmailSettings";
import { SiteSettings } from "./pages/admin/SiteSettings";
import { UserManagement } from "./pages/admin/UserManagement";
import { CustomerLogos } from "./pages/admin/CustomerLogos";
import { Gallery as AdminGallery } from "./pages/admin/Gallery";
import { GalleryPublic } from "./pages/GalleryPublic";
import { Certificates } from "./pages/admin/Certificates";
import { CertificateTemplates } from "./pages/admin/CertificateTemplates";
import { GenerateCertificate } from "./pages/admin/GenerateCertificate";
import { BulkGenerateCertificates } from "./pages/admin/BulkGenerateCertificates";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  console.log('ProtectedRoute - loading:', loading, 'isAuthenticated:', isAuthenticated, 'user:', user);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to login');
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Permission-based Protected Route Component
const PermissionRoute = ({ children, permission, adminOnly = false }) => {
  const { user, isAdmin } = useAuth();

  // Admin users have access to everything
  if (isAdmin) return children;

  // Admin-only routes
  if (adminOnly) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            This page requires administrator privileges.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check specific permission
  if (permission && user?.permissions && !user.permissions[permission]) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this module.
            <br />
            Please contact your administrator.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={
              <>
                <Header />
                <Home />
                <Footer />
              </>
            } />
            <Route path="/training-programs" element={
              <>
                <Header />
                <TrainingPrograms />
                <Footer />
              </>
            } />
            {/* Industry Training Routes */}
            <Route path="/industry-training" element={
              <>
                <Header />
                <IndustryTrainings />
                <Footer />
              </>
            } />
            {/* Custom Corporate Services Page */}
            <Route path="/corporate-training-course-in-india" element={
              <>
                <Header />
                <CorporateServicesDetail />
                <Footer />
              </>
            } />
            <Route path="/industry-training/corporate-services" element={<Navigate to="/corporate-training-course-in-india" replace />} />
            <Route path="/industry-training/:industrySlug" element={
              <>
                <Header />
                <IndustryDetail />
                <Footer />
              </>
            } />
            <Route path="/industry-training/:industrySlug/:trainingSlug" element={
              <>
                <Header />
                <TrainingProgramDetail />
                <Footer />
              </>
            } />
            {/* Dedicated NABH Training Page */}
            <Route path="/nabh-training" element={
              <>
                <Header />
                <NABHTraining />
                <Footer />
              </>
            } />
            <Route path="/industry-training/healthcare/nabh-accreditation-hospital-training" element={<Navigate to="/nabh-training" replace />} />
            {/* Dedicated 5S Training Page */}
            <Route path="/5s-training-in-chennai" element={
              <>
                <Header />
                <FiveSTraining />
                <Footer />
              </>
            } />
            <Route path="/industry-training/manufacturing/5s-training-chennai" element={<Navigate to="/5s-training-in-chennai" replace />} />
            <Route path="/safety-first-aid-training" element={<Navigate to="/industry-training/manufacturing/safety-first-aid-training" replace />} />
            <Route path="/industry-training/manufacturing/workplace-safety-first-aid-training-manufacturing" element={<Navigate to="/industry-training/manufacturing/safety-first-aid-training" replace />} />
            {/* Dedicated AI Training Page */}
            <Route path="/ai-training-program" element={
              <>
                <Header />
                <AITraining />
                <Footer />
              </>
            } />
            <Route path="/industry-training/information-technology/ai-training-program" element={<Navigate to="/ai-training-program" replace />} />
            <Route path="/about" element={
              <>
                <Header />
                <AboutUs />
                <Footer />
              </>
            } />
            <Route path="/contact" element={
              <>
                <Header />
                <Contact />
                <Footer />
              </>
            } />
            <Route path="/validate-certificate" element={
              <>
                <Header />
                <CertificateValidationPage />
                <Footer />
              </>
            } />
            <Route path="/training-calendar" element={
              <>
                <Header />
                <TrainingCalendarPublic />
                <Footer />
              </>
            } />
            <Route path="/training-event/:slug" element={
              <>
                <Header />
                <TrainingDetailPage />
                <Footer />
              </>
            } />
            <Route path="/training/iso-training-services" element={
              <>
                <Header />
                <ManagementSystems />
                <Footer />
              </>
            } />
            <Route path="/training/:standardSlug/:category" element={
              <>
                <Header />
                <TrainingCategoryDetail />
                <Footer />
              </>
            } />
            <Route path="/training/:slug" element={
              <>
                <Header />
                <StandardDetail />
                <Footer />
              </>
            } />
            <Route path="/training/course/:slug" element={
              <>
                <Header />
                <CourseDetail />
                <Footer />
              </>
            } />
            <Route path="/gallery" element={
              <>
                <Header />
                <GalleryPublic />
                <Footer />
              </>
            } />
            <Route path="/placement" element={
              <>
                <Header />
                <Placement />
                <Footer />
              </>
            } />
            <Route path="/training/professional-development-training-programs" element={
              <>
                <Header />
                <ProfessionalDevelopment />
                <Footer />
              </>
            } />
            {/* Legacy route redirect for old URL */}
            <Route path="/training/professional-development" element={<Navigate to="/training/professional-development-training-programs" replace />} />
            <Route path="/training/soft-skills-training-chennai" element={
              <>
                <Header />
                <SoftSkillsTraining />
                <Footer />
              </>
            } />
            <Route path="/training/leadership-and-management-training" element={
              <>
                <Header />
                <LeadershipManagementTraining />
                <Footer />
              </>
            } />
            <Route path="/training/communication-skills-training-chennai" element={
              <>
                <Header />
                <CommunicationSkillsTraining />
                <Footer />
              </>
            } />
            <Route path="/training/faculty-development-program" element={
              <>
                <Header />
                <FacultyDevelopmentProgram />
                <Footer />
              </>
            } />
            <Route path="/training/hei-training" element={
              <>
                <Header />
                <HEITraining />
                <Footer />
              </>
            } />
            <Route path="/Lead-Auditor-Certification-ISO-9001" element={
              <>
                <Header />
                <ISO9001LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/ISO-27001-Lead-Auditor-Course" element={
              <>
                <Header />
                <ISO27001LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/ISO-14001-Lead-Auditor-Course" element={
              <>
                <Header />
                <ISO14001LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/FMEA-Training" element={
              <>
                <Header />
                <FMEATraining />
                <Footer />
              </>
            } />
            <Route path="/Core-Tools-Training" element={
              <>
                <Header />
                <CoreToolsTraining />
                <Footer />
              </>
            } />
            <Route path="/ISO-45001-Lead-Auditor-Course" element={
              <>
                <Header />
                <ISO45001LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/ISO-9001-Awareness-Training" element={
              <>
                <Header />
                <ISO9001Awareness />
                <Footer />
              </>
            } />
            <Route path="/Internal-Auditor-ISO-9001-Training" element={
              <>
                <Header />
                <ISO9001InternalAuditor />
                <Footer />
              </>
            } />
            {/* Legacy route redirect for old URL */}
            <Route path="/ISO-9001-Internal-Auditor-Training" element={<Navigate to="/Internal-Auditor-ISO-9001-Training" replace />} />
            <Route path="/ISO-14001-Awareness-Training" element={
              <>
                <Header />
                <ISO14001Awareness />
                <Footer />
              </>
            } />
            <Route path="/ISO-14001-Internal-Auditor-Course" element={
              <>
                <Header />
                <ISO14001InternalAuditor />
                <Footer />
              </>
            } />
            {/* Legacy route redirect for old URL */}
            <Route path="/ISO-14001-Internal-Auditor-Training" element={<Navigate to="/ISO-14001-Internal-Auditor-Course" replace />} />
            <Route path="/ISO-27001-Awareness-Training" element={
              <>
                <Header />
                <ISO27001Awareness />
                <Footer />
              </>
            } />
            <Route path="/ISO-27001-Internal-Auditor-Course" element={
              <>
                <Header />
                <ISO27001InternalAuditor />
                <Footer />
              </>
            } />
            {/* Legacy route redirect for old URL */}
            <Route path="/ISO-27001-Internal-Auditor-Training" element={<Navigate to="/ISO-27001-Internal-Auditor-Course" replace />} />
            <Route path="/ISO-45001-Awareness-Training" element={
              <>
                <Header />
                <ISO45001Awareness />
                <Footer />
              </>
            } />
            <Route path="/ISO-45001-Internal-Auditor-Training" element={
              <>
                <Header />
                <ISO45001InternalAuditor />
                <Footer />
              </>
            } />
            <Route path="/IATF-16949-Awareness-Training" element={
              <>
                <Header />
                <IATF16949Awareness />
                <Footer />
              </>
            } />
            <Route path="/IATF-16949-Internal-Auditor-Training" element={
              <>
                <Header />
                <IATF16949InternalAuditor />
                <Footer />
              </>
            } />
            <Route path="/IATF-16949-Lead-Auditor-Course" element={
              <>
                <Header />
                <IATF16949LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/training/iatf-16949/lead-auditor" element={<Navigate to="/IATF-16949-Lead-Auditor-Course" replace />} />
            <Route path="/ISO-13485-Awareness-Training" element={
              <>
                <Header />
                <ISO13485Awareness />
                <Footer />
              </>
            } />
            <Route path="/ISO-13485-Internal-Auditor-Training" element={
              <>
                <Header />
                <ISO13485InternalAuditor />
                <Footer />
              </>
            } />
            <Route path="/ISO-13485-Lead-Auditor-Training" element={
              <>
                <Header />
                <ISO13485LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/Lean-Six-Sigma-Certification-Course" element={
              <>
                <Header />
                <LeanSixSigma />
                <Footer />
              </>
            } />
            <Route path="/total-productive-maintenance-course" element={
              <>
                <Header />
                <TotalProductiveMaintenance />
                <Footer />
              </>
            } />
            <Route path="/esg-training-course" element={
              <>
                <Header />
                <ESGTraining />
                <Footer />
              </>
            } />
            <Route path="/ISO-22000-Awareness-Training" element={
              <>
                <Header />
                <ISO22000Awareness />
                <Footer />
              </>
            } />
            <Route path="/ISO-22000-Internal-Auditor-Training" element={
              <>
                <Header />
                <ISO22000InternalAuditor />
                <Footer />
              </>
            } />
            <Route path="/ISO-22000-Lead-Auditor-Course" element={
              <>
                <Header />
                <ISO22000LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/training/iso-22000/lead-auditor" element={<Navigate to="/ISO-22000-Lead-Auditor-Course" replace />} />
            <Route path="/ISO-50001-Awareness-Training" element={
              <>
                <Header />
                <ISO50001Awareness />
                <Footer />
              </>
            } />
            <Route path="/ISO-50001-Internal-Auditor-Course" element={
              <>
                <Header />
                <ISO50001InternalAuditor />
                <Footer />
              </>
            } />
            {/* Legacy route redirect for old URL */}
            <Route path="/ISO-50001-Internal-Auditor-Training" element={<Navigate to="/ISO-50001-Internal-Auditor-Course" replace />} />
            <Route path="/ISO-50001-Lead-Auditor-Course" element={
              <>
                <Header />
                <ISO50001LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/training/iso-50001/lead-auditor" element={<Navigate to="/ISO-50001-Lead-Auditor-Course" replace />} />
            <Route path="/ISO-21001-Awareness-Training" element={
              <>
                <Header />
                <ISO21001Awareness />
                <Footer />
              </>
            } />
            <Route path="/ISO-21001-Internal-Auditor-Training" element={
              <>
                <Header />
                <ISO21001InternalAuditor />
                <Footer />
              </>
            } />
            <Route path="/ISO-21001-Lead-Auditor-Training" element={
              <>
                <Header />
                <ISO21001LeadAuditor />
                <Footer />
              </>
            } />
            <Route path="/training/naac-accreditation-preparation" element={
              <>
                <Header />
                <NAACAccreditationPreparation />
                <Footer />
              </>
            } />
            <Route path="/training/obe-implementation" element={
              <>
                <Header />
                <OBEImplementation />
                <Footer />
              </>
            } />
            <Route path="/training/outcome-based-education-in-india" element={
              <>
                <Header />
                <OBEImplementation />
                <Footer />
              </>
            } />
            <Route path="/training/iso-21001-educational-organizations" element={
              <>
                <Header />
                <ISO21001EducationalOrganizations />
                <Footer />
              </>
            } />
            <Route path="/training/research-methodology" element={
              <>
                <Header />
                <ResearchMethodology />
                <Footer />
              </>
            } />
            <Route path="/training/digital-teaching-tools" element={
              <>
                <Header />
                <DigitalTeachingTools />
                <Footer />
              </>
            } />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={
                <PermissionRoute permission="dashboard">
                  <AdminDashboard />
                </PermissionRoute>
              } />
              <Route path="training-calendar" element={
                <PermissionRoute permission="training_calendar">
                  <TrainingCalendarAdmin />
                </PermissionRoute>
              } />
              <Route path="registrations" element={
                <PermissionRoute permission="registrations">
                  <Registrations />
                </PermissionRoute>
              } />
              <Route path="customer-logos" element={
                <PermissionRoute permission="customer_logos">
                  <CustomerLogos />
                </PermissionRoute>
              } />
              <Route path="gallery" element={
                <PermissionRoute permission="gallery">
                  <AdminGallery />
                </PermissionRoute>
              } />
              <Route path="contact-submissions" element={
                <PermissionRoute permission="contact_submissions">
                  <ContactSubmissions />
                </PermissionRoute>
              } />
              <Route path="email-settings" element={
                <PermissionRoute adminOnly={true}>
                  <EmailSettings />
                </PermissionRoute>
              } />
              <Route path="site-settings" element={
                <PermissionRoute adminOnly={true}>
                  <SiteSettings />
                </PermissionRoute>
              } />
              <Route path="training-categories" element={
                <PermissionRoute adminOnly={true}>
                  <div className="p-8">Training Categories - Coming soon</div>
                </PermissionRoute>
              } />
              <Route path="testimonials" element={
                <PermissionRoute adminOnly={true}>
                  <div className="p-8">Testimonials - Coming soon</div>
                </PermissionRoute>
              } />
              <Route path="blog-posts" element={
                <PermissionRoute adminOnly={true}>
                  <div className="p-8">Blog Posts - Coming soon</div>
                </PermissionRoute>
              } />
              <Route path="certificates" element={
                <PermissionRoute adminOnly={true}>
                  <Certificates />
                </PermissionRoute>
              } />
              <Route path="certificates/templates" element={
                <PermissionRoute adminOnly={true}>
                  <CertificateTemplates />
                </PermissionRoute>
              } />
              <Route path="certificates/generate" element={
                <PermissionRoute adminOnly={true}>
                  <GenerateCertificate />
                </PermissionRoute>
              } />
              <Route path="certificates/bulk-generate" element={
                <PermissionRoute adminOnly={true}>
                  <BulkGenerateCertificates />
                </PermissionRoute>
              } />
              <Route path="users" element={
                <PermissionRoute adminOnly={true}>
                  <UserManagement />
                </PermissionRoute>
              } />
            </Route>
          </Routes>
          <Toaster />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
